import {
  SelfHelpFeedbackAction,
  SelfHelpFeedbackDescriptor,
  SelfHelpFeedbackState,
} from "../interfaces/selfHelpFeedback";

export type SelfHelpFeedbackJourneyIdFactory = () => string;

const createJourneyId = (): string => {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createSelfHelpFeedbackState = (
  descriptor: SelfHelpFeedbackDescriptor,
  journeyIdFactory: SelfHelpFeedbackJourneyIdFactory = createJourneyId
): SelfHelpFeedbackState => ({
  status: "resolution",
  journeyId: journeyIdFactory(),
  descriptorId: descriptor.descriptorId,
  owningSelfHelpTurnId: descriptor.owningSelfHelpTurnId,
});

export const canRevealSelfHelpTicketing = (
  state: SelfHelpFeedbackState
): boolean => state.status === "ticket-ready" || state.status === "fallback";

export const rehydrateSelfHelpFeedbackState = (
  persistedState: SelfHelpFeedbackState | null | undefined,
  descriptor: SelfHelpFeedbackDescriptor,
  journeyIdFactory: SelfHelpFeedbackJourneyIdFactory = createJourneyId
): SelfHelpFeedbackState => {
  const belongsToCurrentSelfHelp =
    persistedState?.descriptorId === descriptor.descriptorId &&
    persistedState?.owningSelfHelpTurnId === descriptor.owningSelfHelpTurnId;

  return belongsToCurrentSelfHelp
    ? persistedState
    : createSelfHelpFeedbackState(descriptor, journeyIdFactory);
};

const isTerminal = (state: SelfHelpFeedbackState): boolean =>
  state.status === "resolved" || state.status === "abandoned";

export const selfHelpFeedbackReducer = (
  state: SelfHelpFeedbackState,
  action: SelfHelpFeedbackAction
): SelfHelpFeedbackState => {
  if (isTerminal(state)) {
    return state;
  }

  if (action.type === "abandon") {
    return {
      status: "abandoned",
      journeyId: state.journeyId,
      descriptorId: state.descriptorId,
      owningSelfHelpTurnId: state.owningSelfHelpTurnId,
    };
  }

  if (action.type === "complete") {
    return {
      status: "resolved",
      journeyId: state.journeyId,
      descriptorId: state.descriptorId,
      owningSelfHelpTurnId: state.owningSelfHelpTurnId,
    };
  }

  switch (state.status) {
    case "resolution":
      if (action.type !== "answerResolution") {
        return state;
      }

      return action.resolved
        ? { ...state, status: "resolved" }
        : { ...state, status: "category" };

    case "category":
      return action.type === "selectCategory"
        ? { ...state, status: "feedback", category: action.category }
        : state;

    case "feedback":
    case "revision":
      if (action.type === "selectCategory") {
        return {
          status: "feedback",
          journeyId: state.journeyId,
          descriptorId: state.descriptorId,
          owningSelfHelpTurnId: state.owningSelfHelpTurnId,
          category: action.category,
        };
      }

      if (action.type !== "submitFeedback") {
        return state;
      }

      if (!action.feedback.trim()) {
        return { ...state, error: action.requiredError };
      }

      return {
        status: "validating",
        journeyId: state.journeyId,
        descriptorId: state.descriptorId,
        owningSelfHelpTurnId: state.owningSelfHelpTurnId,
        category: state.category,
        feedback: action.feedback,
        ...(state.status === "revision"
          ? { validatorGuidance: state.validatorGuidance }
          : {}),
      };

    case "validating":
      if (action.type === "validationNeedsImprovement") {
        return {
          ...state,
          status: "revision",
          validatorGuidance: action.guidance,
        };
      }

      if (action.type === "validationPassed") {
        return {
          ...state,
          status: "ticket-ready",
          primaryFailureReason: action.primaryFailureReason,
          guidanceDisposition: action.guidanceDisposition,
          failedStepNumber: action.failedStepNumber,
        };
      }

      if (action.type === "validationFailed") {
        return {
          ...state,
          status: "fallback",
          fallbackMessage: action.message,
        };
      }

      return state;

    case "fallback":
    case "ticket-ready":
      return state;
  }

  return state;
};

// Generated by Copilot
// updated on: 2026-09-09 | Story 1297509 TASK-002 | Added guarded rehydratable self-help feedback reducer | Time Taken 7 min
// updated on: 2026-09-09 | Story 1297509 TASK-005 | Added exhaustive fallback required by portal noImplicitReturns check | Time Taken 1 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Allowed category correction and reset stale revision validation state | Time Taken 3 min
// updated on: 2026-09-10 | Story 1297509 hardening | Removed reducer-owned English validation copy | Time Taken 1 min
// updated on: 2026-09-14 | 5 lines changed | [Auto Generated] | Store validated primary failure reason in ready state | Time Taken 1 min
// updated on: 2026-10-18 | 8 lines added | [Auto Generated] | Terminalize successful ticket handoff journeys | Time Taken 1 min
